import type { z } from 'zod'

import type {
  getCreateSiteFormSchema,
  getUpdateFinancialInfoFormSchema,
  getUpdateSiteInformationFormSchema,
  getUpdateSiteMarketProgramContractsFormSchema,
  getUpdateSiteNotificationPreferencesFormSchema,
} from '@/features/site/schemas'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { Partner } from '@/openapi/v1/msa-customer-partner-manager/partner'

export interface Site {
  uuid: string
  name: string
  symbolicName?: string
  partner?: Partner
  timezone?: string
  location?: string
  coordinates?: Coordinates
  countryCode?: CountryIdentifier
  address?: string
  siteType?: string
  isTopPlacementPriority?: boolean
  contacts?: SiteContact[]
  notificationPreferences?: NotificationPreferences
  financialInformation?: FinancialInformation
  marketProgramContracts?: SiteMarketProgramContract[]
  contractDate?: string
  status?: SiteStatus
  hubspotCompanyId?: string
}

export enum SiteStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export interface SiteListResponse {
  uuid: string
  name: string
  partner?: Partner
  location?: string
  countryCode?: CountryIdentifier
  address?: string
  status?: SiteStatus
  marketPrograms?: MarketProgram[]
}

export interface CreateSite {
  name: string
  symbolicName: string
  partnerCode: string
  location: string
  timezone: string
  address?: string
  coordinates?: Coordinates
  countryCode?: CountryIdentifier
  siteType?: string
  contacts?: SiteContact[]
  notificationPreferences?: NotificationPreferences
  financialInformation?: FinancialInformation
  marketProgramContracts?: SiteMarketProgramContract[]
  contractDate?: string
}

export interface UpdateSiteInformation {
  name: string
  symbolicName: string
  partnerCode: string
  timezone: string
  location: string
  contractDate?: string
  address?: string
  coordinates?: Coordinates
}

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface SiteContact {
  legacyId?: number
  name: string | null
  phone?: string
  email?: string
  comment?: string
  role?: string
  createdAt?: string
}

export interface NotificationPreferences {
  phoneNumbers?: string[]
  email: string | null
  language: string
  isSmsEnabled: boolean
  isEmailEnabled: boolean
}

export interface FinancialInformation {
  iban: string | null
  ibanHolder: string | null
}

export interface SiteMarketProgramContract {
  marketProgram?: MarketProgram
  revenueSharingFraction?: number | null
  roundingErrorInWatts?: number | null
  soldCapacityAlgorithmType?: 'min' | 'average' | null
}

export interface SiteMarketProgramContractRequest {
  marketProgramId: number
  revenueSharingFraction?: number | null
  roundingErrorInWatts?: number | null
  soldCapacityAlgorithmType?: 'min' | 'average' | null
}

export interface SiteNotificationPreferencesRequest {
  phoneNumbers?: string[]
  email: string | null
  language: string
  isSmsEnabled: boolean
  isEmailEnabled: boolean
}

export interface SiteActivationOrderSettingsRequest {
  isTopPlacementPriority: boolean
}

export type CreateSiteForm = z.infer<ReturnType<typeof getCreateSiteFormSchema>>

export type UpdateSiteInformationForm = z.infer<ReturnType<typeof getUpdateSiteInformationFormSchema>>

export type UpdateSiteFinancialInfoForm = z.infer<ReturnType<typeof getUpdateFinancialInfoFormSchema>>

export type UpdateSiteMarketProgramContractsForm = z.infer<
  ReturnType<typeof getUpdateSiteMarketProgramContractsFormSchema>
>

export type UpdateSiteNotificationPreferencesForm = z.infer<
  ReturnType<typeof getUpdateSiteNotificationPreferencesFormSchema>
>

export type CreateSiteContactRequest = {
  siteUuid: string
  location: string
  contact: SiteContact
}

export type UpdateSiteContactRequest = {
  siteUuid: string
  location: string
  contact: SiteContact
}

export type DeleteSiteContactRequest = {
  siteUuid: string
  contactId: number
  location: string
}
